import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";
import { queryKeys } from "../query-keys";
import {
  ApplicationListResponseSchema,
  ApplicationSchema,
  type ApplicationStatus,
} from "@applicant-reviewer/api";

export interface ApplicationFilters {
  status?: ApplicationStatus;
  jobId?: string;
  campaignId?: string;
  page?: number;
  limit?: number;
}

function buildApplicationsPath(basePath: string, filters?: ApplicationFilters, persona?: "manager" | "candidate"): string {
  const params = new URLSearchParams();
  // The Lambda requires candidateId=me for candidate list queries
  if (persona !== "manager") params.set("candidateId", "me");
  if (filters?.status) params.set("status", filters.status);
  if (filters?.jobId) params.set("jobPostingId", filters.jobId);
  if (filters?.campaignId) params.set("campaignId", filters.campaignId);
  if (filters?.page !== undefined) params.set("page", String(filters.page));
  if (filters?.limit !== undefined) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function useApplications(filters?: ApplicationFilters, persona?: "manager" | "candidate") {
  const basePath = persona === "manager" ? "/manager-applications" : "/applications";
  return useQuery({
    queryKey: queryKeys.applications.list(filters),
    queryFn: async () => {
      const res = await apiClient.get(buildApplicationsPath(basePath, filters, persona), ApplicationListResponseSchema);
      return res.applications;
    },
  });
}

export function useApplication(id: string, persona?: "manager" | "candidate") {
  const basePath = persona === "manager" ? "/manager-applications" : "/applications";
  return useQuery({
    queryKey: queryKeys.applications.detail(id, persona),
    queryFn: () => apiClient.get(`${basePath}/${id}`, ApplicationSchema),
    enabled: Boolean(id),
  });
}
